import React, { useState, useEffect } from 'react';
import { conditionsAPI, getImageUrl } from '../../../services/api';
import ImageUploader from '../Shared/ImageUploader';
import RichTextEditor from '../Shared/RichTextEditor';
import '../Shared/FormStyles.scss';

const ConditionsManager = () => {
  const [conditions, setConditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    content: '',
    isActive: true,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchConditions();
  }, []);

  const fetchConditions = async () => {
    try {
      const response = await conditionsAPI.getAllAdmin();
      setConditions(response.data);
    } catch (err) {
      setError('Failed to fetch conditions');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editing) {
        await conditionsAPI.update(editing._id, formData);
        setSuccess('Condition updated successfully');
      } else {
        await conditionsAPI.create(formData);
        setSuccess('Condition created successfully');
      }
      resetForm();
      fetchConditions();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save condition');
    }
  };

  const handleEdit = (condition) => {
    setEditing(condition);
    setFormData({
      title: condition.title || '',
      description: condition.description || '',
      image: condition.image || '',
      content: condition.content || '',
      isActive: condition.isActive !== undefined ? condition.isActive : true,
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this condition?')) return;
    try {
      await conditionsAPI.delete(id);
      setSuccess('Condition deleted successfully');
      fetchConditions();
    } catch (err) {
      setError('Failed to delete condition');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      title: '',
      description: '',
      image: '',
      content: '',
      isActive: true,
    });
  };

  // Re-indexes the whole list to sequential order values so moves stay
  // correct even if existing records share the same (or default 0) order.
  const moveCondition = async (index, direction) => {
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= conditions.length) return;

    const reordered = [...conditions];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];
    setConditions(reordered);

    try {
      await Promise.all(reordered.map((condition, i) => conditionsAPI.update(condition._id, { order: i })));
      await fetchConditions();
    } catch (err) {
      setError('Failed to reorder conditions');
      fetchConditions();
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="conditions-manager">
      <div className="admin-form">
        <h2>{editing ? 'Edit Condition' : 'Add New Condition'}</h2>
        <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '5px' }}>
          <strong>Note:</strong> Each condition here gets its own page on the website at <code>/conditions/&lt;id&gt;</code>, linked from the card on the Conditions page.
          To manage the simple list of conditions displayed on the home page, use the <strong>"Home Page Conditions List"</strong> tab in the Home Content section.
        </div>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>
          <ImageUploader
            value={formData.image}
            onChange={(url) => setFormData({ ...formData, image: url })}
            label="Card / Hero Image"
          />
          <div className="form-group">
            <label>Description (shown on the card)</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>
          <div className="form-group">
            <label>Full Content (shown on the condition's own page)</label>
            <RichTextEditor
              value={formData.content}
              onChange={(html) => setFormData({ ...formData, content: html })}
              placeholder="Leave blank to show default placeholder content for this condition."
            />
          </div>
          <div className="form-group">
            <label>
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
              Active
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="dashboard-btn btn-primary">
              {editing ? 'Update' : 'Create'}
            </button>
            {editing && (
              <button type="button" className="dashboard-btn btn-secondary" onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="data-table" style={{ marginTop: '30px' }}>
        <h2 style={{ padding: '20px', margin: 0 }}>All Conditions</h2>
        {conditions.length === 0 ? (
          <div className="empty-state">No conditions found</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Move</th>
                <th>Image</th>
                <th>Title</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {conditions.map((condition, index) => (
                <tr key={condition._id}>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button type="button" className="btn-edit" onClick={() => moveCondition(index, -1)} disabled={index === 0} style={{ padding: '2px 8px' }}>↑</button>
                      <button type="button" className="btn-edit" onClick={() => moveCondition(index, 1)} disabled={index === conditions.length - 1} style={{ padding: '2px 8px' }}>↓</button>
                    </div>
                  </td>
                  <td>
                    {condition.image && (
                      <img src={getImageUrl(condition.image)} alt={condition.title} style={{ width: '50px', height: '50px', objectFit: 'cover', borderRadius: '4px' }} />
                    )}
                  </td>
                  <td>{condition.title}</td>
                  <td>{condition.description}</td>
                  <td>{condition.isActive ? 'Active' : 'Inactive'}</td>
                  <td>
                    <div className="table-actions">
                      <button className="btn-edit" onClick={() => handleEdit(condition)}>
                        Edit
                      </button>
                      <button className="btn-delete" onClick={() => handleDelete(condition._id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ConditionsManager;
